<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Layout from '../components/layout/Layout.vue'
import StellarBackground from '../components/animations/StellarBackground.vue'
import CyberLoading from '../components/ui/CyberLoading.vue'
import ImageViewer from '../components/ui/ImageViewer.vue'
import altercoin from '../assets/menus/altercoin.svg'

const { t } = useI18n()
const isLoading = ref(true)
const selectedImage = ref('')
const isImageViewerOpen = ref(false)

const closeImageViewer = () => {
  isImageViewerOpen.value = false
  selectedImage.value = ''
}

onMounted(() => {
  setTimeout(() => {
    isLoading.value = false
  }, 1000)
})
</script>

<template>
  <Layout>
    <div class="catering-wrapper">
      <StellarBackground backColor="#2d3434" :circle-offset="{x: -350, y: 100}"/>
      <div class="catering-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="catering-page" :class="{ 'content-loaded': !isLoading }">
          <!-- Header Section -->
          <div class="catering-header">
            <h1>{{ t('catering.title') }}</h1>
            <p>{{ t('catering.subtitle') }}</p>
          </div>

          <!-- Main Content -->
          <div class="content-section">
            <!-- How it Works -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-information</v-icon>
                {{ t('catering.howItWorks.title') }}
              </h2>
              <v-card class="content-card" elevation="0">
                <v-card-text>
                  <div class="content-text" v-html="t('catering.howItWorks.content')"></div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Opening Hours -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-clock-outline</v-icon>
                {{ t('catering.hours.title') }}
              </h2>
              <v-card class="content-card" elevation="0">
                <v-card-text>
                  <div class="content-text" v-html="t('catering.hours.content')"></div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Menu Cards -->
            <div class="menu-grid">
              <!-- Drinks Menu -->
              <div class="menu-item">
                <v-card class="menu-card" elevation="0">
                  <v-card-text>
                    <div class="menu-wrapper">
                      <div class="drinks-menu">
                        <img class="menu-logo" src="@/assets/logos/brand_icon_element_flag_webp.webp" alt="FLAG LOGO">
                        <div class="menu-header">
                          <h1 class="drinks-title">DRINKS MENU</h1>
                          <p class="drinks-subtitle">ALTERBYTE</p>
                        </div>

                        <!-- Soft Drinks -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              SOFT DRINKS
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Chaudfontaine Sparkling', price: 1.00 },
                              { name: 'Chaudfontaine Reine', price: 1.00 },
                              { name: 'Coca Cola', price: 1.50 },
                              { name: 'Coca Cola Zero', price: 1.50 },
                              { name: 'Fanta Orange', price: 1.50 },
                              { name: 'Lipton Ice Tea', price: 2.00 },
                              { name: 'Sprite', price: 1.50 },
                              { name: 'Schweppes Tonic', price: 1.50 },
                              { name: 'Red Bull', price: 2.00 },
                              { name: 'Orange Juice', price: 1.00 },
                              { name: 'Apple Juice', price: 1.00 }
                            ]" :key="index" class="menu-item">
                              <span class="item-name">{{ item.name }}</span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Beer -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              BEER<span>(16+)</span>
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Jupiler Pils', price: 1.50 },
                              { name: 'Jupiler Pils 0,0%', price: 1.00 },
                              { name: 'Duvel', price: 2.50 },
                              { name: 'Mort Subite Kriek', price: 2.00 },
                              { name: 'Desperados', price: 2.50 }
                            ]" :key="index" class="menu-item">
                              <span class="item-name">{{ item.name }}</span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Practical Information -->
                        <div class="menu-footer">
                          <h3 class="footer-title">
                            PRACTICAL INFORMATION
                          </h3>
                          <div class="footer-content">
                            <div class="info-items">
                              <div class="info-item">
                                <img src="@/assets/menus/altercoinLight.svg" class="altercoin-icon" alt="altercoin">
                                <span>ALTERCOINS</span>
                              </div>
                              <div class="info-item">
                                <v-icon>mdi-leaf</v-icon>
                                <span>Vegetarian</span>
                              </div>
                              <div class="info-item">
                                <v-icon>mdi-compare-horizontal</v-icon>
                                <span>1 ALTERCOIN = 1.75 EUR</span>
                              </div>
                            </div>
                            <div class="logo-section">
                              <img src="@/assets/logos/brand_icon_element_dark_webp.webp" class="alterlan-logo" alt="alterlan logo">
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Food Menu -->
              <div class="menu-item">
                <v-card class="menu-card" elevation="0">
                  <v-card-text>
                    <div class="menu-wrapper">
                      <div class="drinks-menu">
                        <img class="menu-logo" src="@/assets/logos/brand_icon_element_flag_webp.webp" alt="FLAG LOGO">
                        <div class="menu-header">
                          <h1 class="drinks-title">FOOD MENU</h1>
                          <p class="drinks-subtitle">ALTERBYTE</p>
                        </div>

                        <!-- Breakfast -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              BREAKFAST<span>(8:00H UNTIL 11:00H)</span>
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Bacon & Egg Sandwich', price: 2.00 },
                              { name: 'Croissant', price: 1.00 },
                              { name: 'Chocolate Croissant', price: 1.50 },
                              { name: 'Curryroll', price: 1.00 },
                              { name: 'Donut', price: 1.00 }
                            ]" :key="index" class="menu-item">
                              <span class="item-name-wrapper">
                                <span class="item-name">{{ item.name }}</span>
                              </span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Meals -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              MEALS<span>(11:00H UNTIL 23:00H)</span>
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Hamburger', price: 1.50 },
                              { name: 'Cheeseburger', price: 2.00 },
                              { name: 'Bicky Burger', price: 2.50 },
                              { name: 'Vegi Burger', price: 2.00, vegetarian: true },
                              { name: 'ALTERLAN Burger', price: 4.00 },
                              { name: 'ALTERLAN Vegi Burger', price: 4.00, vegetarian: true },
                              { name: 'Cheese Sandwich', price: 1.50 },
                              { name: 'Ham Sandwich', price: 1.50 },
                              { name: 'Ham & Cheese Sandwich', price: 2.00 },
                              { name: 'Healthy Sandwich', price: 2.00, vegetarian: true },
                              { name: 'Tomato Soup', price: 2.00, vegetarian: true },
                              { name: 'Fries', price: 1.50 },
                              { name: 'Fries Special', price: 2.50 },
                              { name: 'Fries Bicky', price: 3.00 },
                              { name: 'Fries Stew', price: 3.50 },
                              { name: 'Spaghetti Bolognaise', price: 3.00 },
                              { name: 'Pasta Arrabiata', price: 3.00, vegetarian: true },
                              { name: 'Pasta Carbonnara', price: 3.00 },
                              { name: 'Assortment Pizza', price: 5.00 }
                            ]" :key="index" class="menu-item">
                              <span class="item-name-wrapper">
                                <v-icon v-if="item.vegetarian" class="vegi-icon" size="small">mdi-leaf</v-icon>
                                <span class="item-name">{{ item.name }}</span>
                              </span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Snacks -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              SNACKS<span>(8:00H UNTIL 03:00H)</span>
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Bitterballs (5x)', price: 1.50 },
                              { name: 'Curryworst', price: 1.00 },
                              { name: 'Curryworst Special', price: 2.00 },
                              { name: 'Mexicano', price: 1.50 },
                              { name: 'Chicken Nuggets (5x)', price: 1.50 },
                              { name: 'Mini Rolls (5x)', price: 2.50, vegetarian: true },
                              { name: 'Mozzarella Sticks (5x)', price: 1.50, vegetarian: true },
                              { name: 'Cheese Croquettes (2x)', price: 1.50, vegetarian: true }
                            ]" :key="index" class="menu-item">
                              <span class="item-name-wrapper">
                                <v-icon v-if="item.vegetarian" class="vegi-icon" size="small">mdi-leaf</v-icon>
                                <span class="item-name">{{ item.name }}</span>
                              </span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- General -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              GENERAL<span>(11:00H UNTIL 08:00H)</span>
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Aiki Noodles', price: 2.00 },
                              { name: 'Royco Soup', price: 1.00 },
                              { name: 'Croque Monsieur', price: 2.00 },
                              { name: 'Chips', price: 1.00 },
                              { name: 'Fruit', price: 1.00, vegetarian: true }
                            ]" :key="index" class="menu-item">
                              <span class="item-name-wrapper">
                                <v-icon v-if="item.vegetarian" class="vegi-icon" size="small">mdi-leaf</v-icon>
                                <span class="item-name">{{ item.name }}</span>
                              </span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Supplements -->
                        <div class="menu-section">
                          <div class="section-header">
                            <h3 class="section-title">
                              SUPPLEMENTS
                            </h3>
                            <span class="price-title">Price</span>
                          </div>
                          <div class="menu-items">
                            <div v-for="(item, index) in [
                              { name: 'Supplement Bread', price: 1.00 },
                              { name: 'Supplement Smos', price: 0.50 },
                              { name: 'Supplement Sauce', price: 0.50 }
                            ]" :key="index" class="menu-item">
                              <span class="item-name">{{ item.name }}</span>
                              <span class="item-price">{{ item.price.toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Practical Information -->
                        <div class="menu-footer">
                          <h3 class="footer-title">
                            PRACTICAL INFORMATION
                          </h3>
                          <div class="footer-content">
                            <div class="info-items">
                              <div class="info-item">
                                <img src="@/assets/menus/altercoinLight.svg" class="altercoin-icon" alt="altercoin">
                                <span>ALTERCOINS</span>
                              </div>
                              <div class="info-item">
                                <v-icon>mdi-leaf</v-icon>
                                <span>Vegetarian</span>
                              </div>
                              <div class="info-item">
                                <v-icon>mdi-compare-horizontal</v-icon>
                                <span>1 ALTERCOIN = 1.75 EUR</span>
                              </div>
                            </div>
                            <div class="logo-section">
                              <img src="@/assets/logos/brand_icon_element_dark_webp.webp" class="alterlan-logo" alt="alterlan logo">
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>

            <div class="bottom-links d-none d-md-flex">
              <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
              <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
              <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Image Viewer -->
      <ImageViewer
        :image-src="selectedImage"
        :is-open="isImageViewerOpen"
        @close="closeImageViewer"
      />
    </div>
  </Layout>
</template>

<style scoped>
.catering-wrapper {
  position: relative;
  width: 100%;
  overflow-x: hidden;
  background-color: #1a1a1a;
  padding: 2rem 0;
}

.catering-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  overflow-y: auto;
  min-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
}

.catering-page {
  opacity: 1;
  transition: opacity 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.catering-header {
  text-align: center;
  position: relative;
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.catering-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.catering-header p {
  font-size: 1.2rem;
  color: #ccc;
  margin: 1rem auto 0;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
  max-width: 1200px;
}

.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.section-container {
  margin-bottom: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  max-width: 1400px;
  padding: 0 0rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.section-title {
  display: flex;
  align-items: center;
  color: #76c4ae;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: .5rem;
  font-family: 'Poppins', sans-serif;
}

.section-title span {
  margin-left: 0.2rem;
  margin-bottom: 0.6rem;
  font-size: 0.6rem;
  justify-self: end;
  align-self: end;

  font-weight: 400;
  color: #ffffff;
}

.price-title {
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  min-width: 70px;
  font-family: 'Poppins', sans-serif;
}

.price-title::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  position: relative;
  top:0;
  left: -2px; 
  background-image: url('../assets/menus/altercoin.svg');
  background-size: contain;
  background-repeat: no-repeat;
}

.section-icon {
  margin-right: 1rem;
}

.content-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  border-radius: 4px;
  margin: 1rem 0;
  padding: 2rem;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-card:hover {
  box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.content-text {
  text-align: left;
  line-height: 1.8;
}

.content-text :deep(p) {
  margin-bottom: 1rem;
}

.content-text :deep(ul) {
  list-style-type: none;
  padding: 0;
}

.content-text :deep(li) {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.content-text :deep(li)::before {
  content: "•";
  color: #76c4ae;
  position: absolute;
  left: 0;
  font-size: 1.2em;
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 0;
  margin-top: 2rem;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

.nav-link :deep(br) {
  display: block;
  content: "";
  margin: 0.1rem 0;
}

.menu-wrapper {
  width: 100%;
  margin: 0;
  padding: 0;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 1fr);
  gap: 2rem;
  width: 100%;
  margin-bottom: 4rem;
}

.menu-item {
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
}

.menu-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  border-radius: 4px;
  margin: 1rem 0;
  padding: 2rem;
}

.menu-card:hover {
  box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.menu-image {
  width: 100%;
  height: auto;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.altercoin-icon {
  width: 20px;
  height: 20px;
}

.menu-image:hover {
  transform: scale(1.02);
}

.drinks-menu {
  background-color: #2d3434;
  border-radius: 8px 8px 8px 8px;
  margin-bottom: 0;
  color: white;
  width: 100%;
  background-image: url('../assets/objects/circle_element_circles_light_2K.webp');
  background-size: 2000px;
  background-position: -1000px 0px;
  background-repeat: no-repeat;
  background-clip: border-box;
  background-blend-mode: overlay;
}

.menu-logo {
  position: absolute;
  top: 3rem;
  left: 5rem;
  width: 50px;
}

.menu-header {
  text-align: center;
}

.drinks-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;
  margin-top: 1rem;
}

.drinks-subtitle {
  font-family: 'Airborne GP', sans-serif;
  font-size: 2.0rem;
  color: #ccc;
  padding: 0;
  margin-bottom: 2rem;
  line-height: 0.8;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.menu-section {
  padding: 0rem 3rem;
  margin-bottom: 4rem;
  width: 100%;
}

.age-note {
  font-size: 0.8rem;
  opacity: 0.8;
}

.menu-items {
  display: grid;
  gap: 0.1rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.1rem 0;
  margin: 0.25rem 0;
}

.item-name-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.item-name {
  color: white;
  font-size: 0.9rem;
}

.vegi-icon {
  color: #df824d;
  position: absolute;
  left: -1.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
}

.item-price {
  color: #ffffff;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 52px;
}

.menu-footer {
  padding: 2rem 3rem;
  border-radius: 0 0 8px 8px;
  margin-top: 0rem !important;
  background: #76c4ae;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 1.5rem;
}

.info-items {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #ffffff;
  font-size: 0.9rem;
  letter-spacing: 0.05rem;
}

.logo-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 3rem;
  margin-bottom: 0rem;
  opacity: 0.6;
}

.alterlan-logo {
  width: 125px;
  height: auto;
  opacity: 0.8;
  margin-top: -1rem;
}

.footer-title {
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.1rem;
}

@media (max-width: 768px) {
  .info-items {
    gap: 1rem;
    flex-direction: column;
  }

  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }

  .catering-header h1 {
    font-size: 2.0rem !important;
  }

  .catering-header p {
    font-size: 1rem;
  }

  .menu-card {
    padding: 0rem;
  }

  .section-title span {
    display: none;
  }

  .catering-container {
    padding: 0rem 1rem;
  }

  .section-container {
    padding: 0rem;
  }

  .menu-logo {
    top: 1rem;
    left: 1.5rem;
    z-index: 1;
  };



  .section-title {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    margin-bottom: 1rem;
    padding-left: 0 !important;
  }

  .menu-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 0rem;
  }

  .menu-image {
    margin-bottom: 1rem;
  }

  .menu-section {
    padding: 0rem 1.8rem;
  }

  .drinks-subtitle {
    font-size: 1rem;
  }

  .drinks-title {
    font-size: 0.8rem;
  }

  .section-title {
    font-size: 1.2rem;
  }

  .item-name {
    font-size: 0.8rem;
  }

  .price-title {
    font-size: 1.2rem;
  }

  .logo-section {
    display: none;
  }

}
</style>
